/**
 * The mark a rehearsal takes once it has started: who preached, or that nobody did.
 *
 * **Rewritten by C-002** (`FND-1591`, 2026-10-06, `docs/specs/features/C-002/spec.md` §3.4). Until
 * then this was a port of `legacy/frontend/src/screens/SermonMark.tsx`, «Проповедь была?» with
 * «была» / «не было» over the one brother the rota had put on the date. Nobody is put on a date
 * any more: three brothers prepare, and whoever came preached — so the mark names him.
 *
 * The question itself, «Кто проповедовал?», is the caller's: the card says it as its title and
 * the sheet as its label.
 *
 * What the old file got right stays. The same gesture the roster on a running rehearsal has, one
 * tap inside a list. The regent or the keeper sets it and never the preacher. And it draws what
 * has been said, not only what may be said: the answer stays on screen, and pressing another is
 * how it is corrected.
 */

import { useState } from 'react';

import type { BrotherRow, SermonStatus } from '@contracts/index';

export default function SermonMark({
  picked,
  preacher,
  preparing,
  brothers,
  busy,
  onMark,
}: {
  /** What was said about this rehearsal. `planned` until somebody says something. */
  picked: SermonStatus;
  /** Who was named, beside `done`. */
  preacher: number | null;
  /** The brothers preparing a sermon this week, offered first. */
  preparing: BrotherRow[];
  /** Every brother, in the queue's order, behind «Другой брат…». */
  brothers: BrotherRow[];
  busy: boolean;
  /** A brother, or null for «не было». */
  onMark: (memberId: number | null) => void;
}) {
  const [everybody, setEverybody] = useState(false);
  const said = picked !== 'planned';
  const offered = preparing.map((one) => one.member_id);
  // Whoever preached stays in view even when he was not on the list: a mark
  // that names somebody the buttons do not show reads as no mark at all.
  const shown = everybody
    ? brothers
    : [
        ...preparing,
        ...brothers.filter((one) => one.member_id === preacher && !offered.includes(one.member_id)),
      ];

  function mark(memberId: number | null, on: boolean, status?: string) {
    return {
      className: on
        ? 'live-mark live-mark--picked'
        : said
          ? 'live-mark live-mark--blank'
          : 'live-mark',
      'data-status': on ? status : undefined,
      'data-press': 'wash',
      'aria-pressed': on,
      disabled: busy,
      onClick: () => onMark(memberId),
    };
  }

  return (
    <>
      <ul className="live">
        {shown.map((one) => (
          <li key={one.member_id} className="live-row">
            <span className="live-body">
              <span className="live-name">{one.display_name}</span>
            </span>
            <button
              {...mark(one.member_id, picked === 'done' && preacher === one.member_id, 'on_time')}
            >
              проповедовал
            </button>
          </li>
        ))}
      </ul>
      <div className="row-buttons">
        {!everybody && brothers.length > shown.length && (
          // The same small mark as «не было» beside it: two answers to one
          // question, and a full-width button here dwarfed the other (Vitali,
          // 2026-10-07).
          <button
            className={said ? 'live-mark live-mark--blank' : 'live-mark'}
            data-press="wash"
            disabled={busy}
            onClick={() => setEverybody(true)}
          >
            Другой брат…
          </button>
        )}
        <button {...mark(null, picked === 'missed', 'absent')}>не было</button>
      </div>
    </>
  );
}
